import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Button, CircularProgress, LinearProgress } from '@mui/material'
import { H3, H5 } from '../../../../layouts/TextStyles'
import { courseApiSlice } from '../../../../features/course/courseSlice'
import { capitalize } from '../../../../utils/functions'
import { courseSubjectPriceApi } from '../../../../features/course/courseSubjectPrice'
import {
    CourseSubjectPricePurchaseState,
    PriceState,
    PurchasedCourseTopicState,
    SelectedPurchaseSubjectState,
    getCourseSubjectPricesParams,
} from '../../../../features/types'
import SingleSelect from '../../../../components/muiComponents/singleSelect'
import { paymentApi } from '../../../../features/payment/payment'
import { UseScript } from '../../../../utils/UseScript'
import PackageDiscountInfo from './components/PackageDiscountInfo'
import CheckoutStepper from './components/CheckoutStepper'
import SummaryCard from './components/SummaryCard'
import PaymentMethod from './components/PaymentMethod'
import {
    Content,
    Header,
    Title,
    Wrapper,
    flexWrap,
    languageBox,
    paymentBox,
    purchaseBox,
} from './style'
import { SelectedSubjectState } from './types'

const CheckoutPage = () => {
    const [searchParams] = useSearchParams()
    const queryLanguage = searchParams.get('language')
    const queryCourseTopicId = searchParams.get('courseTopicId')
    const queryCourseSubjectId = searchParams.get('courseSubjectId')
    const queryParamsExists = Boolean(
        queryLanguage && queryCourseTopicId && queryCourseSubjectId,
    )
    const navigate = useNavigate()
    const { formatMessage, locale } = useIntl()
    const { coursePriceId, courseId } = useParams()
    const [activeStep, setActiveStep] = useState(queryParamsExists ? 2 : 0)
    const [selectedSubjects, setSelectedSubjects] = useState<
        SelectedSubjectState[]
    >([])
    const [selectedSections, setSelectedSections] = useState([])
    const [selectedLanguage, setSelectedLanguage] = useState('')
    const { halyk }: any = UseScript(
        'https://epay.homebank.kz/payform/payment-api.js',
        'halyk',
    )
    const disableFreeStart = !selectedLanguage || !selectedSections.length

    const payload = {
        auth: {
            access_token: '',
            expires_in: '',
            refresh_token: '',
            scope: '',
            token_type: '',
        },
        invoiceId: 0,
        backLink: 'https://edutest.kz/purchase-exam-success',
        failureBackLink: 'https://edutest.kz/purchase-exam-failure',
        postLink: '',
        failurePostLink: '',
        language: `${locale}`,
        description: '',
        terminal: '',
        amount: 0,
        currency: '',
        email: '',
    }

    const [price, setPrice] = useState<PriceState>(null)
    const { data: course = null, isFetching: isCourseFetching } =
        courseApiSlice.useGetCourseByIdQuery(+courseId)
    const { data: purchaseResponse = null, isFetching: isPurchaseFetching } =
        courseSubjectPriceApi.useGetPurchaseDetailsByCoursePriceQuery(
            +coursePriceId,
        )

    const [getCourseSubjectPrices, { isLoading: isLoadingPrices }] =
        courseSubjectPriceApi.useGetCourseSubjectPricesMutation()
    const [getToken] = paymentApi.useGetTokenMutation()
    const [startFreeCourse, { isLoading: isStartingFreeCourse }] =
        courseSubjectPriceApi.useStartFreeCourseMutation()

    const onSubjectClick = (
        subject: SelectedPurchaseSubjectState,
        courseSubjectPrice: CourseSubjectPricePurchaseState,
    ) => {
        if (selectedSubjects.some((item) => item.id === subject.id)) {
            const filteredSubejcts = selectedSubjects.filter(
                (item) => item.id !== subject.id,
            )
            // remove subject from list
            setSelectedSubjects(filteredSubejcts)

            return
        }

        setSelectedSubjects([
            ...selectedSubjects,
            {
                ...subject,
                courseSubjectId: subject.courseSubjectId,
                courseSubjectPrice,
                selectedTopics: [],
                isSelected: true,
            },
        ])
    }

    const onTopicClick = (
        topic: PurchasedCourseTopicState,
        subjectId: number,
        topics: PurchasedCourseTopicState[],
    ) => {
        const subjects = selectedSubjects.map((subject) => {
            let isSelected = subject.isSelected

            if (subject.id === subjectId) {
                if (isSelected) {
                    const filteredTopics = topics.filter(
                        (item) => item.courseTopicId !== topic.courseTopicId,
                    )

                    return {
                        ...subject,
                        selectedTopics: filteredTopics,
                        isSelected: false,
                    }
                } else if (
                    subject.selectedTopics.some(
                        (item) => item.courseTopicId === topic.courseTopicId,
                    )
                ) {
                    const filteredTopics = subject.selectedTopics?.filter(
                        (item) => item.courseTopicId !== topic.courseTopicId,
                    )

                    // set subject isSelected = false, if topic is not selected
                    if (filteredTopics.length < subject.selectedTopics.length) {
                        isSelected = false
                    }

                    // remove topics from list
                    return {
                        ...subject,
                        selectedTopics: filteredTopics,
                        isSelected,
                    }
                }

                // set subject isSelect = true, if all topics selected
                if (topics.length === subject.selectedTopics.length + 1) {
                    isSelected = true
                } else {
                    isSelected = false
                }

                return {
                    ...subject,
                    selectedTopics: [...subject.selectedTopics, topic],
                    isSelected,
                }
            }

            return {
                ...subject,
            }
        })

        setSelectedSubjects(subjects)
    }

    const handleSetSelectedTopics = (
        topics: PurchasedCourseTopicState[],
        subjectId: number,
    ) => {
        let subjects = []
        if (queryParamsExists) {
            subjects = selectedSubjects.map((subject) => {
                if (subject.courseSubjectId === +queryCourseSubjectId) {
                    const selectedTopic = topics.find(
                        (topic) => topic.courseTopicId === +queryCourseTopicId,
                    )
                    if (selectedTopic) {
                        return {
                            ...subject,
                            selectedTopics: [{ ...selectedTopic }],
                        }
                    }
                }

                return subject
            })
        } else {
            subjects = selectedSubjects.map((subject) => {
                if (subject.id === subjectId) {
                    return { ...subject, selectedTopics: topics }
                }

                return subject
            })
        }

        setSelectedSubjects(subjects)
    }

    const handleLanguage = (language: string) => {
        setSelectedLanguage(language)
    }

    const handleLanguageAndIncrementStep = (language: string) => {
        handleLanguage(language)
        setActiveStep((prevStep) => prevStep + 1)
    }

    const getPrices = async (section: getCourseSubjectPricesParams[]) => {
        try {
            const result = await getCourseSubjectPrices(section).unwrap()
            setPrice(result)
        } catch (error) {}
    }

    const handleFreeStart = async () => {
        const courseSubjectPriceIds: number[] = selectedSections.map(
            (selectedSection) => selectedSection.courseSubjectPriceId,
        )

        try {
            await startFreeCourse({
                languageEnum: selectedLanguage,
                courseSubjectPriceIds,
            }).unwrap()
            toast.success(formatMessage({ id: 'enrolledToCourse' }))
            navigate('/my-courses')
        } catch (error) {}
    }

    const formatToPurchaseSelector = () => {
        const sections = selectedSubjects.reduce((result, subject) => {
            const isSectionSelected =
                subject.isSelected ||
                subject.selectedTopics.length ||
                subject.courseSubjectId === +queryCourseSubjectId

            if (isSectionSelected) {
                const courseTopicIds = subject.selectedTopics.map(
                    (topic) => topic.courseTopicId,
                )

                result.push({
                    courseSubjectPriceId: subject.courseSubjectPrice.id,
                    topicCount: calculateTopicCount(subject),
                    courseTopicIds: !subject.isSelected ? courseTopicIds : null,
                })
            }

            return result
        }, [])

        setSelectedSections(sections)

        return sections
    }

    const calculateTopicCount = (subject: SelectedSubjectState) => {
        if (queryParamsExists) {
            return subject.selectedTopics.length
        }
        return !subject.isSelected ? subject.selectedTopics.length : 0
    }

    const handlePay = async (method: string) => {
        //console.log('method', method)
        const params = {
            language: selectedLanguage,
            paymentType: method,
        }

        console.log('params', params)
        console.log('sections', selectedSections)
        const paymentObject: any = await getToken({
            params,
            body: selectedSections,
        })
        console.log('r')
        if (method === 'kaspi') {
            window.location.href = `https://kaspi.kz/pay/EduTest?service_id=5633&8952=${paymentObject.data}`
        } else {
            payload.auth = paymentObject.data.token
            payload.invoiceId = paymentObject.data.invoice_id
            payload.amount = paymentObject.data.amount
            payload.currency = paymentObject.data.currency
            payload.email = paymentObject.data.email
            payload.postLink = paymentObject.data.post_link
            payload.failurePostLink = paymentObject.data.failure_post_link
            payload.terminal = paymentObject.data.terminal
            payload.description = paymentObject.data.description

            // halyk?.pay(paymentObject)
        }
        console.log('res', paymentObject)
        console.log('payload', payload)
    }

    useEffect(() => {
        const sections = formatToPurchaseSelector()
        if (sections.length) {
            getPrices(sections)
        } else {
            setPrice(null)
        }
    }, [selectedSubjects])

    useEffect(() => {
        if (queryLanguage && queryCourseTopicId) {
            setSelectedLanguage(queryLanguage)
        }
    }, [queryLanguage, queryCourseTopicId])

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>
                            {purchaseResponse &&
                                formatMessage(
                                    { id: 'buyCourse' },
                                    {
                                        value: purchaseResponse?.coursePrice[
                                            `title${capitalize(locale)}`
                                        ],
                                    },
                                )}
                        </H3>
                    </Title>

                    {/* <pre>{JSON.stringify(selectedSubjects, null, 2)}</pre> */}

                    {activeStep === 1 && (
                        <Box sx={languageBox}>
                            <H5>{formatMessage({ id: 'language' })}: </H5>

                            <SingleSelect
                                id="language"
                                name="language"
                                placeholder={formatMessage({
                                    id: 'language',
                                })}
                                value={selectedLanguage}
                                handleChange={(e) =>
                                    handleLanguage(e.target.value)
                                }
                                menuItems={[
                                    {
                                        id: 'RU',
                                        name: formatMessage({ id: 'ru' }),
                                    },
                                    {
                                        id: 'KZ',
                                        name: formatMessage({ id: 'kz' }),
                                    },
                                ]}
                                small={true}
                            />
                        </Box>
                    )}
                </Header>

                {isCourseFetching || isPurchaseFetching ? (
                    <LinearProgress />
                ) : (
                    purchaseResponse && (
                        <>
                            <PackageDiscountInfo
                                packages={
                                    purchaseResponse.coursePackageDiscounts
                                }
                                promotion={purchaseResponse.coursePromotion}
                                course={course}
                            />

                            <Box sx={purchaseBox}>
                                {purchaseResponse && (
                                    <CheckoutStepper
                                        coursePriceId={+coursePriceId}
                                        queryCourseSubjectId={
                                            +queryCourseSubjectId
                                        }
                                        activeStep={activeStep}
                                        purchaseResponse={purchaseResponse}
                                        selectedLanguage={selectedLanguage}
                                        selectedSubjects={selectedSubjects}
                                        queryParamsExists={queryParamsExists}
                                        setActiveStep={setActiveStep}
                                        handleLanguage={
                                            handleLanguageAndIncrementStep
                                        }
                                        setSelectedSubjects={
                                            setSelectedSubjects
                                        }
                                        onSelectTopics={handleSetSelectedTopics}
                                        onSubjectClick={onSubjectClick}
                                        onTopicClick={onTopicClick}
                                    />
                                )}

                                <Box sx={paymentBox}>
                                    <SummaryCard
                                        selectedSubjects={selectedSubjects}
                                        coursePrice={
                                            purchaseResponse?.coursePrice
                                        }
                                        price={price}
                                        isLoading={isLoadingPrices}
                                    />

                                    {price && price.discountPrice > 0 && (
                                        <Box>
                                            <PaymentMethod onPay={handlePay} />

                                            {selectedSubjects.some(
                                                (subject) =>
                                                    subject.isSelected ||
                                                    subject.courseSubjectId ===
                                                        +queryCourseSubjectId,
                                            ) && (
                                                <Button
                                                    variant="contained"
                                                    size="large"
                                                    sx={{ mt: 3 }}
                                                    onClick={handleFreeStart}
                                                    disabled={disableFreeStart}
                                                    fullWidth
                                                >
                                                    {isStartingFreeCourse ? (
                                                        <CircularProgress
                                                            size={20}
                                                            style={{
                                                                marginRight:
                                                                    '8px',
                                                            }}
                                                        />
                                                    ) : (
                                                        formatMessage({
                                                            id: 'freeStart',
                                                        })
                                                    )}
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </>
                    )
                )}
                <Box sx={flexWrap}></Box>
            </Content>
        </Wrapper>
    )
}

export default CheckoutPage
